import { useState } from 'react';

import { Link, useParams } from '@tanstack/react-router';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Separator } from '@fade/ui';

import { useSession } from '../lib/auth';
import { useComments, useCreateComment, useDeleteComment } from '../lib/queries/comments';
import { useRememberState, useToggleRemember } from '../lib/queries/remember';
import { useLegacy } from '../lib/queries/legacies';

export function LegacyDetailPage() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const { data: session } = useSession();

  const { data: legacy, isPending } = useLegacy(id);
  const { data: rememberState } = useRememberState(id);
  const { data: comments } = useComments(id);

  const toggle = useToggleRemember(id ?? '');
  const postComment = useCreateComment(id ?? '');
  const deleteComment = useDeleteComment(id ?? '');

  const [commentBody, setCommentBody] = useState('');

  if (!id) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-16">
        <p className="text-muted-foreground">Missing story id.</p>
      </main>
    );
  }

  if (isPending) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-16">
        <p className="text-muted-foreground">Loading…</p>
      </main>
    );
  }

  if (!legacy) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-16">
        <h1 className="font-serif text-2xl">Story not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          It may have been archived or never published.
        </p>
      </main>
    );
  }

  const remembered = rememberState?.remembered ?? false;

  return (
    <main className="mx-auto max-w-2xl px-6 py-16">
      <article>
        <header className="mb-6">
          <h1 className="font-serif text-4xl font-light leading-tight">{legacy.title}</h1>
          <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {legacy.publishedAt
                ? new Date(legacy.publishedAt).toLocaleDateString()
                : 'Draft'}
            </span>
            <span>❤️ {legacy.rememberCount}</span>
          </div>
        </header>

        <div className="whitespace-pre-wrap font-serif text-lg leading-relaxed text-foreground/90">
          {legacy.body}
        </div>

        <Separator className="my-8" />

        <div className="flex items-center gap-3">
          {session ? (
            <Button
              variant={remembered ? 'secondary' : 'default'}
              onClick={() => toggle.mutate(remembered ? 'forget' : 'remember')}
              disabled={toggle.isPending}
            >
              {remembered ? 'Remembered' : 'Remember ❤️'}
            </Button>
          ) : (
            <Link to="/sign-in" search={{ redirect: `/legacies/${legacy.id}` }}>
              <Button variant="secondary">Sign in to remember</Button>
            </Link>
          )}
        </div>
      </article>

      <section className="mt-12 space-y-6">
        <h2 className="font-serif text-xl font-medium">Comments</h2>

        {session ? (
          <form
            className="space-y-2"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!commentBody.trim()) return;
              await postComment.mutateAsync(commentBody.trim());
              setCommentBody('');
            }}
          >
            <Label htmlFor="comment" className="sr-only">
              Comment
            </Label>
            <Input
              id="comment"
              placeholder="Add a comment…"
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
            />
            <Button type="submit" disabled={postComment.isPending || !commentBody.trim()}>
              Post
            </Button>
          </form>
        ) : (
          <p className="text-sm text-muted-foreground">
            <Link to="/sign-in" className="underline">
              Sign in
            </Link>{' '}
            to leave a comment.
          </p>
        )}

        <div className="space-y-3">
          {(comments?.items ?? []).map((c) => (
            <Card key={c.id}>
              <CardHeader>
                <CardTitle className="text-sm font-normal text-muted-foreground">
                  {new Date(c.createdAt).toLocaleString()}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm">{c.body}</p>
                {session?.user.id === c.authorId ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteComment.mutate(c.id)}
                    disabled={deleteComment.isPending}
                  >
                    Delete
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </main>
  );
}